import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { AlertCircle, Save, Check, Key, Settings as SettingsIcon, ArrowLeft, Crown, Mic, User, ChevronDown, LogOut, Eye, EyeOff, Play, Loader2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const SuperAdminVoiceAgent = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [openaiKey, setOpenaiKey] = useState('');
  const [selectedModel, setSelectedModel] = useState('gpt-4o-mini-realtime-preview-2024-12-17');
  const [selectedVoice, setSelectedVoice] = useState('echo');
  const [existingSettings, setExistingSettings] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [playingVoice, setPlayingVoice] = useState(null);
  const audioRef = React.useRef(null);

  const backendUrl = '';

  // Available models with descriptions
  const models = [
    { 
      id: 'gpt-4o-mini-realtime-preview-2024-12-17', 
      name: 'GPT-4o Mini Realtime', 
      description: 'Cost-effective, fast responses (Recommended)'
    },
    { 
      id: 'gpt-4o-realtime-preview-2024-12-17', 
      name: 'GPT-4o Realtime', 
      description: 'Most capable, higher cost'
    }
  ];

  // Available voices with descriptions (startup-friendly, balanced male/female)
  // Note: Some voices don't have TTS preview but work perfectly in Realtime API
  const voices = [
    // Male voices (Realtime API only)
    { id: 'echo', name: 'Echo', description: 'Warm and approachable', gender: 'Male', hasPreview: true },
    { id: 'ash', name: 'Ash', description: 'Smooth and calm', gender: 'Male', hasPreview: false },
    { id: 'ballad', name: 'Ballad', description: 'Confident and professional', gender: 'Male', hasPreview: false },
    { id: 'verse', name: 'Verse', description: 'Natural and conversational', gender: 'Male', hasPreview: false },
    
    // Female voices
    { id: 'shimmer', name: 'Shimmer', description: 'Bright and energetic', gender: 'Female', hasPreview: true },
    { id: 'coral', name: 'Coral', description: 'Friendly and engaging', gender: 'Female', hasPreview: false },
    { id: 'sage', name: 'Sage', description: 'Clear and trustworthy', gender: 'Female', hasPreview: false },
    
    // Neutral/Balanced
    { id: 'alloy', name: 'Alloy', description: 'Neutral and balanced', gender: 'Neutral', hasPreview: true }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/admin/openai-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.has_key) {
        setExistingSettings(response.data);
        if (response.data.model) setSelectedModel(response.data.model);
        if (response.data.voice) setSelectedVoice(response.data.voice);
      }
    } catch (error) {
      console.error('Error fetching OpenAI settings:', error);
    }
  };

  const toggleKeyVisibility = () => {
    setShowKey(!showKey);
  };

  const playVoiceSample = async (voiceId) => {
    try {
      setPlayingVoice(voiceId);
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${backendUrl}/api/admin/voice-preview`,
        { voice: voiceId },
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
          },
          responseType: 'blob'
        }
      );

      // Create audio from blob
      const audioUrl = URL.createObjectURL(response.data);
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.play();
        
        audioRef.current.onended = () => {
          setPlayingVoice(null);
          URL.revokeObjectURL(audioUrl);
        };
      }
    } catch (error) {
      console.error('Error playing voice sample:', error);
      setMessage({ 
        type: 'error', 
        text: 'Failed to play voice sample. Make sure OpenAI API key is configured.' 
      });
      setPlayingVoice(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${backendUrl}/api/admin/openai-settings`,
        { 
          api_key: openaiKey,
          model: selectedModel,
          voice: selectedVoice
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setMessage({ type: 'success', text: 'Voice agent settings saved successfully!' });
      
      // Clear API key field only
      setOpenaiKey('');
      
      // Refresh settings
      await fetchSettings();
      
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to save settings' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-appbg">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin')}
                className="flex items-center space-x-2"
                data-testid="back-to-admin"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Admin Dashboard</span>
              </Button>
              
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl flex items-center justify-center">
                  <Mic className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Voice Agent Settings</h1>
                  <p className="text-sm text-slate-600">Configure OpenAI Realtime API for AI voice assessments</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Badge className="bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-100 hover:!text-purple-700">
                <Crown className="w-3 h-3 mr-1" />
                Super Admin
              </Badge>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="flex items-center gap-2 border-slate-200 hover:bg-slate-50 hover:text-slate-900"
                  >
                    <User className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-900 max-w-[150px] truncate">
                      {user?.full_name || user?.email?.split('@')[0]}
                    </span>
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium text-slate-900">{user?.email}</p>
                      <p className="text-xs text-slate-500">Super Administrator</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={logout}
                    className="text-red-600 focus:text-red-600 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">

        {/* Current Settings Display */}
        {existingSettings?.has_key && (
          <Card className="mb-6 border-navy-200 bg-navy-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-navy-900">
                <Check className="w-5 h-5 text-green-600" />
                Current Configuration
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="font-semibold">OpenAI API Key:</span>
                  <span className="ml-2 font-mono text-slate-600">
                    {existingSettings.api_key_masked}
                  </span>
                </div>
                <div>
                  <span className="font-semibold">Model:</span>
                  <span className="ml-2 text-slate-600">
                    {models.find(m => m.id === existingSettings.model)?.name || existingSettings.model || 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="font-semibold">Voice:</span>
                  <span className="ml-2 text-slate-600">
                    {voices.find(v => v.id === existingSettings.voice)?.name || existingSettings.voice || 'Not set'}
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-3">
                  Last updated: {new Date(existingSettings.updated_at).toLocaleString()}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Setup Instructions */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <SettingsIcon className="w-5 h-5 text-navy-600" />
              Setup Instructions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal list-inside space-y-3 text-slate-700">
              <li>
                Go to <a href="https://platform.openai.com/" target="_blank" rel="noopener noreferrer" className="text-navy-600 underline">OpenAI Platform</a> and sign in to your account
              </li>
              <li>Navigate to API Keys section</li>
              <li>Click "Create new secret key"</li>
              <li>Copy your API key (starts with <code className="bg-slate-100 px-1 rounded">sk-</code>)</li>
              <li>Paste the key below and save</li>
            </ol>
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>Note:</strong> This OpenAI API key will be used for the Realtime Voice API to power AI-driven compliance assessments.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Settings Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-navy-600" />
              API Configuration
            </CardTitle>
            <CardDescription>
              Enter your OpenAI API key for voice assessments
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">
                  OpenAI API Key *
                </label>
                <input
                  type="password"
                  value={openaiKey}
                  onChange={(e) => setOpenaiKey(e.target.value)}
                  placeholder="sk-..."
                  required
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-500"
                />
                <p className="text-xs text-slate-500 mt-1">Your OpenAI API key for Realtime Voice API</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">
                  Model *
                </label>
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-500 bg-white"
                >
                  {models.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name} - {model.description}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Choose GPT-4o Mini for lower costs or GPT-4o for best performance
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-3">
                  Voice Selection (4 Male / 3 Female / 1 Neutral) *
                </label>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  {voices.map((voice) => (
                    <div
                      key={voice.id}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all flex flex-col ${
                        selectedVoice === voice.id
                          ? 'border-navy-600 bg-navy-50'
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                      onClick={() => setSelectedVoice(voice.id)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-slate-900">{voice.name}</h4>
                            <Badge className={
                              voice.gender === 'Male' 
                                ? 'bg-blue-100 text-blue-700 text-xs' 
                                : voice.gender === 'Female'
                                ? 'bg-pink-100 text-pink-700 text-xs'
                                : 'bg-slate-100 text-slate-700 text-xs'
                            }>
                              {voice.gender}
                            </Badge>
                            {!voice.hasPreview && (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">
                                Realtime Only
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-600">{voice.description}</p>
                          {!voice.hasPreview && (
                            <p className="text-xs text-amber-600 mt-1">No preview - works in actual assessment</p>
                          )}
                        </div>
                        {selectedVoice === voice.id && (
                          <Check className="w-5 h-5 text-navy-600 flex-shrink-0" />
                        )}
                      </div>
                      
                      {/* Spacer to push button to bottom */}
                      <div className="flex-grow"></div>
                      
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (voice.hasPreview) {
                            playVoiceSample(voice.id);
                          }
                        }}
                        disabled={!voice.hasPreview || playingVoice === voice.id}
                        className={`w-full mt-2 text-xs ${!voice.hasPreview ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        {playingVoice === voice.id ? (
                          <>
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            Playing...
                          </>
                        ) : !voice.hasPreview ? (
                          <>
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Preview Not Available
                          </>
                        ) : (
                          <>
                            <Play className="w-3 h-3 mr-1" />
                            Preview Sample
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
                
                <p className="text-xs text-slate-500">
                  Click on a voice card to select it, then click "Preview Sample" to hear it
                </p>
                
                {/* Hidden audio element for playback */}
                <audio ref={audioRef} style={{ display: 'none' }} />
              </div>

              {message.text && (
                <div className={`flex items-start gap-2 p-4 rounded-lg ${
                  message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                }`}>
                  {message.type === 'success' ? (
                    <Check className="w-5 h-5 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  )}
                  <p className="text-sm">{message.text}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-navy-600 hover:bg-navy-700 text-white py-3 font-semibold flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Settings
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="mt-6 border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-1">Security Note</p>
                <p>
                  Your API keys are encrypted before storage and never exposed to users. The private key is only used on the backend server.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SuperAdminVoiceAgent;
